<template>
  <NoteSection
    label="Wound Site Observations"
  >
    <v-col
      v-if="isTestGroup"
      cols="12"
      class="d-flex"
      style="background-color: #eff5fb; border-radius: 6px"
    >
      <icon-value
        :icon="icons.mdiInformation"
        color="#4688ec"
        style="line-height: 38px"
      ></icon-value>
      <div class="text-left pl-3">
        <p class="my-2" style="font-size: 16px; color: black">
          Please refer to the wound site where the amniotic membrane graft was applied.
        </p>
      </div>
    </v-col>
    <label class="font-weight-bold py-4">
      1. How would you describe the pain/discomfort at your wound site today?
    </label>
    <radio-buttons
      v-model="wound_site_pain_level"
      :items="tenPointScale"
      class="col-sm-12"
      required
    />
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      2. Please rate the level of redness around the wound site:
    </label>
    <radio-buttons
      v-model="amnioResearch.redness_level"
      :items="tenPointScale"
      class="col-sm-12"
      required
    ></radio-buttons>
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      3. Please rate the level of swelling around the wound site:
    </label>
    <radio-buttons
      v-model="amnioResearch.swelling_level"
      :items="tenPointScale"
      class="col-sm-12"
      required
    ></radio-buttons>
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      4. Is the skin around the wound site warmer than other areas of the body?
    </label>
    <radio-buttons
      v-model="amnioResearch.is_warmness"
      :items="query"
      class="col-sm-12"
      required
    ></radio-buttons>
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      5. Have you noticed any itching at or around the wound site?
    </label>
    <radio-buttons
      v-model="amnioResearch.is_itching"
      :items="query"
      class="col-sm-12"
      required
    ></radio-buttons>
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      6. Have you noticed any discharge (fluid or pus) from your wound?
    </label>
    <radio-buttons
      v-model="amnioResearch.discharge_type"
      :items="woundDischarge"
      class="col-sm-12"
      columns="2"
      required
    ></radio-buttons>
    <label class="font-weight-bold pb-3">
      6.1 If yes, please describe the amount:
    </label>
    <radio-buttons
      v-model="amnioResearch.discharge_amount"
      :items="woundDischargeAmount"
      class="col-sm-12"
      columns="1"
      :disabled="amnioResearch.discharge_type === 'No discharge'"
      required
    ></radio-buttons>
    <v-divider class="col-sm-12 solid-divider"></v-divider>
    <label class="font-weight-bold py-3">
      7. Have you noticed any new or unusual odor from your wound?
    </label>
    <check-boxes
      v-model="wound_site_odor"
      :items="odors"
      class="col-sm-12"
      columns="1"
      return-object
      radio-buttons
      required
    ></check-boxes>
    <v-divider class="col-sm-12 solid-divider"></v-divider>

    <label
      v-if="wasAmnioTreated && isTestGroup"
      class="font-weight-bold py-3"
    >
      8. How would you describe the appearance of the amniotic membrane graft itself during your one-week follow-up?
    </label>
    <radio-buttons
      v-if="wasAmnioTreated && isTestGroup"
      v-model="amnioResearch.appearance"
      :items="woundAppearance"
      class="col-sm-12"
      columns="1"
      required
    ></radio-buttons>
    <text-area
      v-if="amnioResearch.appearance === 'Other' && wasAmnioTreated && isTestGroup"
      v-model="amnioResearch.appearance_other"
      label="Please describe..."
      class="col-sm-12"
      required
      counter="500"
      maxlength="500"
    ></text-area>
  </NoteSection>
</template>

<script>
import { mapGetters } from 'vuex'
import { mdiInformation } from '@mdi/js'

export default {
  name: 'WoundSiteObservations',
  components: {},
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    wasAmnioTreated: {
      type: Boolean,
      default: false,
    },
    woundSitePainLevel: {
      type: Number,
      default: null,
    },
    woundSiteOdor: {
      type: Array,
      default: () => [],
    },
    isTestGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tenPointScale: [
        { title: 0 },
        { title: 1 },
        { title: 2 },
        { title: 3 },
        { title: 4 },
        { title: 5 },
        { title: 6 },
        { title: 7 },
        { title: 8 },
        { title: 9 },
        { title: 10 },
      ],
      isWarmer: '',
      isItching: '',
      woundDischarge: [
        { title: 'No discharge' },
        { title: 'Yes, clear/yellowish fluid' },
        { title: 'Yes, cloudy/milky fluid' },
        { title: 'Yes, thick/green/foul-smelling pus' },
        { title: 'Yes, blood-tinged fluid' },
        { title: 'Yes, bright red blood' },
      ],
      woundDischargeAmount: [
        { title: 'Small amount (e.g., damp dressing)' },
        { title: 'Moderate amount (e.g., dressing saturated, but not dripping)' },
        { title: 'Large amount (e.g., dressing saturated and dripping)' },
      ],
      woundAppearance: [
        { title: 'Still fully visible and intact' },
        { title: 'Partially visible, some areas have dissolved/integrated' },
        { title: 'Not visible, appears fully dissolved/integrated' },
        { title: 'Other' },
      ],
      wound_site_pain_level: this.woundSitePainLevel,
      wound_site_odor: this.woundSiteOdor,
      icons: {
        mdiInformation,
      },
    }
  },
  computed: {
    ...mapGetters('baseData', ['query']),
    ...mapGetters('encounters', ['odors']),
    amnioResearch: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
  },
  watch: {
    amnioResearch: {
      deep: true,
      handler(newVal) {
        if (newVal.discharge_type === 'No discharge') {
          this.amnioResearch.discharge_amount = null
        }
        if (newVal.appearance !== 'Other') {
          this.appearance_other = null
        }
      },
    },
    wound_site_pain_level: {
      handler(newVal) {
        this.$emit('update:woundSitePainLevel', newVal)
      },
    },
    wound_site_odor: {
      handler(newVal) {
        this.$emit('update:woundSiteOdor', newVal)
      },
    },
  },
}
</script>
